/**
 * DevTools Panel — shell flottant, draggable, resizable, dark theme.
 *
 * ────────────────────────────────────────────────────────────────────────
 *  COMMENT AJOUTER UN ONGLET (Option A — token multi-provide)
 * ────────────────────────────────────────────────────────────────────────
 *  1. Crée un composant standalone (ex: DevToolsStateTabComponent).
 *  2. Dans `app.config.ts` (ou un fichier de providers dev-only) :
 *
 *     import { DEV_TOOLS_TAB_COMPONENTS } from './core/dev-tools/dev-tools.types';
 *     import { DevToolsStateTabComponent } from './core/dev-tools/tabs/state-tab.component';
 *
 *     providers: [
 *       {
 *         provide: DEV_TOOLS_TAB_COMPONENTS,
 *         multi: true,
 *         useValue: {
 *           tab: { id: 'state', label: 'State', icon: 'ph ph-database', order: 10 },
 *           component: DevToolsStateTabComponent,
 *         },
 *       },
 *     ]
 *
 *  3. Le shell enregistre automatiquement la registration dans le service
 *     et rend le composant actif via NgComponentOutlet.
 * ────────────────────────────────────────────────────────────────────────
 *  PUSHER UN EVENT
 *     inject(DevToolsService).pushEvent({
 *       source: 'task-ws', type: 'TaskUpdated', level: 'info', payload: {...}
 *     });
 * ────────────────────────────────────────────────────────────────────────
 */
import { CommonModule, NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Optional,
  Inject,
  OnInit,
  Type,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DevToolsService } from './dev-tools.service';
import {
  DEV_TOOLS_TAB_COMPONENTS,
  DevToolsTabRegistration,
} from './dev-tools.types';

type DragMode = 'move' | 'resize' | null;

@Component({
  selector: 'app-dev-tools-panel',
  standalone: true,
  imports: [CommonModule, NgComponentOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dev-tools-panel.component.html',
  styleUrl: './dev-tools-panel.component.scss',
})
export class DevToolsPanelComponent implements OnInit {
  protected readonly service = inject(DevToolsService);

  private readonly registrations: DevToolsTabRegistration[];

  // Map id -> component pour NgComponentOutlet
  private readonly _componentMap = new Map<string, Type<unknown>>();

  // Drag / resize state
  private _mode = signal<DragMode>(null);
  private _startX = 0;
  private _startY = 0;
  private _startLayout = { x: 0, y: 0, w: 0, h: 0 };

  protected readonly activeComponent = computed<Type<unknown> | null>(() => {
    const id = this.service.activeTabId();
    return this._componentMap.get(id) ?? null;
  });

  protected readonly eventCount = computed(() => this.service.events().length);

  constructor(
    @Optional()
    @Inject(DEV_TOOLS_TAB_COMPONENTS)
    regs: DevToolsTabRegistration[] | null
  ) {
    this.registrations = regs ?? [];
  }

  ngOnInit(): void {
    if (!this.service.enabled) return;
    for (const reg of this.registrations) {
      this._componentMap.set(reg.tab.id, reg.component);
      this.service.registerTab(reg.tab);
    }
  }

  // ------------------------------------------------------------------
  //   Keyboard shortcuts (host)
  // ------------------------------------------------------------------
  @HostListener('document:keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    if (!this.service.enabled) return;
    // Ctrl+Shift+D
    if (ev.ctrlKey && ev.shiftKey && (ev.key === 'D' || ev.key === 'd')) {
      ev.preventDefault();
      this.service.toggle();
      return;
    }
    if (ev.key === 'Escape' && this.service.isOpen()) {
      this.service.close();
    }
  }

  // ------------------------------------------------------------------
  //   Tab selection
  // ------------------------------------------------------------------
  selectTab(id: string): void {
    this.service.setActiveTab(id);
  }

  // ------------------------------------------------------------------
  //   Drag (title bar) + Resize (SE corner)
  // ------------------------------------------------------------------
  startDrag(ev: MouseEvent): void {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return;
    if (ev.button !== 0) return;
    ev.preventDefault();
    this._mode.set('move');
    this._startX = ev.clientX;
    this._startY = ev.clientY;
    this._startLayout = { ...this.service.position() };
    this.attachWindowListeners();
  }

  startResize(ev: MouseEvent): void {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return;
    if (ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();
    this._mode.set('resize');
    this._startX = ev.clientX;
    this._startY = ev.clientY;
    this._startLayout = { ...this.service.position() };
    this.attachWindowListeners();
  }

  private attachWindowListeners(): void {
    const move = (e: MouseEvent) => this.onWindowMouseMove(e);
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      this._mode.set(null);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }

  private onWindowMouseMove(ev: MouseEvent): void {
    const mode = this._mode();
    if (!mode) return;
    const dx = ev.clientX - this._startX;
    const dy = ev.clientY - this._startY;

    if (mode === 'move') {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const { w, h } = this._startLayout;
      const nx = Math.max(0, Math.min(this._startLayout.x + dx, vw - 80));
      const ny = Math.max(0, Math.min(this._startLayout.y + dy, vh - 40));
      this.service.updatePosition({ x: nx, y: ny, w, h });
    } else {
      const nw = this._startLayout.w + dx;
      const nh = this._startLayout.h + dy;
      this.service.updatePosition({ w: nw, h: nh });
    }
  }

  // ------------------------------------------------------------------
  //   Header buttons
  // ------------------------------------------------------------------
  toggleMinimize(): void {
    this.service.toggleMinimized();
  }

  clearEvents(): void {
    this.service.clearEvents();
  }

  close(): void {
    this.service.close();
  }

  openFab(): void {
    this.service.open();
  }

  protected badge(tabId: string): number | null {
    const tab = this.service.tabs().find((t) => t.id === tabId);
    if (!tab?.badgeCount) return null;
    try {
      const n = tab.badgeCount();
      return n > 0 ? n : null;
    } catch {
      return null;
    }
  }

  protected trackTab = (_: number, t: { id: string }) => t.id;
}
